import type { ConsignmentDetail, ConsignmentInboundItem, ConsignmentOutboundItem } from '@cuito/types';
import { apiClient } from '../lib/apiClient';
import type { Paginated } from './catalog';

/**
 * 寄售台账的三个调用（`04-api-contract.md` §14 端点表 1–3；UI 规范 550 行的 `/consignments`）。
 *
 * ## 视图类型**一律**从 `@cuito/types` re-export
 *
 * §14.1/§14.2/§14.3 的响应形状在 `packages/types/src/consignment.ts` 里已经有一份（链 A Task 1）。
 * 这里再抄一遍就会在字段增删时漂移，而漂移的那一份**不会有任何编译错误**。
 *
 * ## 金额与费率**字符串透传**
 *
 * `commissionRate` 四位小数、`expectedAmount` / `payableAmount` / `settledAmount` 两位小数，
 * 都是服务端算好的字符串（§1.7）。本层**不做** `Number()` / `toFixed` / 加减 ——
 * 前端的第二份金额算法就是第二份口径（本仓附录 D 第 15 条 + Phase 6 的 `LedgerSummaryCards` 注释）。
 * 余额算法的唯一实现是 `@cuito/types` 的 `consignmentOutstandingCents`（服务端调用它），
 * 前端**只显示**下发的字段。
 *
 * ## 两个视角的参数名**不同**
 *
 * 借出（§14.1）筛 `holderTenantId` + `hasSold`；受托（§14.2）筛 `ownerTenantId`、**无 `hasSold`**
 * （契约原文：「**无 `hasSold`**」）。把这个差异写进一个纯函数（`consignmentQueryOf`）而不是散在 JSX 里，
 * 是为了让它**能被单测钉住** —— 两个 tab 各写一遍筛选拼装，迟早有一边带上对方的字段。
 */

export type {
  ConsignmentDetail,
  ConsignmentDirection,
  ConsignmentEntryView,
  ConsignmentInboundItem,
  ConsignmentLocationRef,
  ConsignmentOutboundItem,
  ConsignmentProductRef,
  ConsignmentTenantRef,
} from '@cuito/types';

/** `GET /consignments/outbound`（§14.1） */
export interface ConsignmentOutboundQuery {
  holderTenantId?: string;
  productId?: string;
  locationId?: string;
  /** 只列 `quantitySold > 0` 的行；**不传**与 `false` 等价（契约只定义 `'true'`/`'false'`） */
  hasSold?: boolean;
  page?: number;
  pageSize?: number;
}

/** `GET /consignments/inbound`（§14.2，**无 `hasSold`**） */
export interface ConsignmentInboundQuery {
  ownerTenantId?: string;
  productId?: string;
  locationId?: string;
  page?: number;
  pageSize?: number;
}

export type ConsignmentTab = 'outbound' | 'inbound';

/**
 * 筛选表单的**串行化形状**：每个字段都是字符串/布尔，空串 = 不筛。
 *
 * 与契约查询分开的理由与 `LedgerFilterInput` 相同：`<select>` 给的是字符串，而契约要的是 uuid；
 * 两种形状混在一个对象里，「空串」与「不传」就会在请求里变成两回事。
 */
export interface ConsignmentFilterInput {
  /** 借出 tab = 受托方（`holderTenantId`）；受托 tab = 货主（`ownerTenantId`） */
  counterpartyTenantId: string;
  /** `仅看已售` —— **只在借出 tab 有效**（§14.2 没有这个参数） */
  hasSold: boolean;
  productId: string;
  locationId: string;
}

export const EMPTY_CONSIGNMENT_FILTERS: ConsignmentFilterInput = {
  counterpartyTenantId: '',
  hasSold: false,
  productId: '',
  locationId: '',
};

/** 契约 §1.3：`pageSize` 默认 20、上限 100 */
export const CONSIGNMENT_PAGE_SIZE = 20;

/**
 * 筛选表单 → 契约查询参数（**tab 决定参数名**；空串一律丢弃）。
 *
 * ⚠️ `hasSold` **只在 `tab === 'outbound'` 且为 `true` 时**出现：受托视角没有这个字段，
 * 带上它会让服务端多一个未知参数（Nest 默认 `whitelist` 下被剥掉，
 * 但请求日志与缓存键会两说）。
 */
export function consignmentQueryOf(
  filters: ConsignmentFilterInput,
  tab: ConsignmentTab,
): ConsignmentOutboundQuery | ConsignmentInboundQuery {
  const query: ConsignmentOutboundQuery & ConsignmentInboundQuery = {};
  if (filters.productId !== '') query.productId = filters.productId;
  if (filters.locationId !== '') query.locationId = filters.locationId;

  if (tab === 'outbound') {
    if (filters.counterpartyTenantId !== '') query.holderTenantId = filters.counterpartyTenantId;
    if (filters.hasSold) query.hasSold = true;
  } else {
    if (filters.counterpartyTenantId !== '') query.ownerTenantId = filters.counterpartyTenantId;
  }

  return query;
}

/**
 * 三个调用（契约 §14 端点表 1–3）。
 *
 * `detail` 的路径**必须**拼在 `/consignments/` 之后（服务端把 `:id` 声明在静态段之后）；
 * 前端拼错的代价是运行时的 404 而不是编译错误。
 */
export const consignmentsApi = {
  /** 分页包裹（§14.1） */
  outbound: (params: ConsignmentOutboundQuery = {}) =>
    apiClient.get<Paginated<ConsignmentOutboundItem>>('/consignments/outbound', { params }),

  /** 分页包裹（§14.2，**不是裸数组**） */
  inbound: (params: ConsignmentInboundQuery = {}) =>
    apiClient.get<Paginated<ConsignmentInboundItem>>('/consignments/inbound', { params }),

  /** 单条明细（§14.3，含 `ownerTenant` 与 `entries`） */
  detail: (id: string) => apiClient.get<ConsignmentDetail>(`/consignments/${id}`),
};
